'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { VioAvatar } from './VioAvatar';

const SEEN_KEY = 'lexvio:vio-launcher-seen';
const AUTO_OPEN_DELAY_MS = 1800;

/**
 * Floating "ask Vio" widget for marketing visitors — mounted once from
 * PageShell.tsx and once from Sections.tsx (the two marketing-chrome
 * roots; the homepage doesn't use PageShell, see its own comment).
 * Never rendered inside the authenticated app, which has its own
 * AssistantPanel / AssistantBar for this job.
 */
export function VioLauncher() {
  const [open, setOpen] = useState(false);

  // Auto-open once, on a visitor's first page view. The effect below
  // records that as "seen" the moment it happens, so a later mount (next
  // page, next visit) reads the flag and skips this.
  useEffect(() => {
    let seen = true;
    try {
      seen = localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      seen = false;
    }
    if (seen) return undefined;
    const timer = setTimeout(() => setOpen(true), AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // Private mode / storage disabled — worst case it auto-opens again later.
    }
  }, [open]);

  return (
    <div className="vio-launcher">
      {open && (
        <div className="vio-launcher-bubble">
          <button
            aria-label="Dismiss"
            className="vio-launcher-close"
            type="button"
            onClick={() => setOpen(false)}
          >
            ×
          </button>
          <div className="vio-launcher-who">Vio</div>
          <div className="vio-launcher-msg">
            Hi, I&apos;m <em>Vio</em>. How may I help?
          </div>
          <Link className="vio-launcher-cta" href="/scan" onClick={() => setOpen(false)}>
            Try the free scanner →
          </Link>
        </div>
      )}
      <button
        aria-expanded={open}
        aria-label={open ? 'Close Vio' : 'Chat with Vio'}
        className="vio-launcher-fab"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <VioAvatar animate={!open} size={64} />
      </button>
    </div>
  );
}
